import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { form, required } from '@angular/forms/signals';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skip, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  HelpDocumentDto,
  HelpState,
  HelpTypeEnum,
} from '@ups/xplat/base/in-app-help';
import { BaseComponent } from '@ups/xplat/core';

interface TooltipModel {
  element: string | null;
  title: string | null;
  body: string | null;
}

@Component({
  selector: 'help-editor-tooltip',
  templateUrl: './editor-tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditorTooltipComponent extends BaseComponent implements OnInit {
  @Input() document: HelpDocumentDto;

  @Output() complete = new EventEmitter<void>();

  // WI 31692: signal form (was an UntypedFormGroup). Seeded null, never undefined,
  // so every key keeps its field node.
  readonly model = signal<TooltipModel>({
    element: null,
    title: null,
    body: null,
  });
  readonly f = form(this.model, (p) => {
    required(p.element);
    required(p.title);
    required(p.body);
  });
  documents: Observable<{ [path: string]: HelpDocumentDto[] }>;

  constructor(store: Store) {
    super();

    this.documents = store.select(HelpState.selectDocumentDictionary);

    // skip(1) on each: the first emission is the ngOnInit seed, which
    // valueChanges never emitted.
    toObservable(this.f.element().value)
      .pipe(skip(1), takeUntil(this.destroy$), withLatestFrom(this.documents))
      .subscribe(([path, documents]) => {
        let existing: HelpDocumentDto | null | undefined = null;
        if (documents[path!]) {
          existing = documents[path!].find(
            (x) => x.type === HelpTypeEnum.Tooltip
          );
        }
        if (existing) {
          Object.assign(this.document, existing);
          this.model.update((m) => ({
            ...m,
            title: this.document.title ?? null,
            body: this.document.html ?? null,
          }));
        } else {
          this.document.path = path!;
        }
      });

    toObservable(this.f.body().value)
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((x) => {
        this.document.html = x!;
      });

    toObservable(this.f.title().value)
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((x) => {
        this.document.title = x!;
      });
  }

  /**
   * Bug 35897: kendo-editor is hand-bound, not [formField]. A signal form echoes writeValue back
   * after the editor's own change, and Kendo then rebuilds the document from a stale value,
   * throwing the caret to the end on every keystroke. help-anchor="body" keeps the help id the
   * [formField] binding gave it.
   */
  setBody(body: string): void {
    this.model.update((m) => ({ ...m, body }));
  }

  ngOnInit() {
    this.model.set({
      element: this.document?.path ?? null,
      title: this.document?.title ?? null,
      body: this.document?.html ?? null,
    });
  }
}
